<template>
  <!-- PrimeVue Toast - positioned at top-center -->
  <Toast position="top-center" />
  <!-- Separate group: replacing the message slot on the main toast would lose its default layout. -->
  <Toast position="top-center" group="action">
    <template #message="{ message }">
      <div class="global-message-action" data-testid="global-message-action">
        <strong>{{ message.summary }}</strong>
        <span>{{ message.detail }}</span>
        <Button
          :label="message.actionLabel"
          size="small"
          data-testid="global-message-action-btn"
          @click="runAction(message)"
        />
      </div>
    </template>
  </Toast>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../eventBus';
import Toast from 'primevue/toast';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { log } from '../utils/DebugLogger';

type MessageType = 'info' | 'success' | 'warning' | 'error';
type ToastSeverity = 'info' | 'success' | 'warn' | 'error';

interface MessagePayload {
  message: string;
  type?: MessageType;
  duration?: number;
}

/** A message with one button that re-emits `event` on the bus when clicked. */
interface ActionPayload {
  message: string;
  actionLabel: string;
  event: string;
  payload?: unknown;
  type?: MessageType;
}

interface ErrorPayload {
  message: string;
  duration?: number;
}

const toast = useToast();
const { t } = useI18n();

const mapTypeToPrimevueSeverity = (type: MessageType): ToastSeverity => {
  switch (type) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warn';
    case 'error':
      return 'error';
    case 'info':
    default:
      return 'info';
  }
};

/**
 * Get a human-readable summary for each message type
 * @param {string} type - Message type
 * @returns {string} Translated summary text
 */
const getSummaryForType = (type: MessageType): string => {
  switch (type) {
    case 'success':
      return t('Sucesso');
    case 'warning':
      return t('Atenção');
    case 'error':
      return t('Erro');
    case 'info':
    default:
      return t('Info');
  }
};

/**
 * Show a global message using PrimeVue Toast
 * @param {string} message - The message to display
 * @param {string} type - Message type: 'info', 'success', 'warning', 'error'
 * @param {number} duration - Auto-hide duration in milliseconds (0 = sticky, no auto-hide)
 */
const showMessage = (message: string, type: MessageType = 'info', duration: number = 5000): void => {
  const severity = mapTypeToPrimevueSeverity(type);

  toast.add({
    severity: severity,
    summary: getSummaryForType(type),
    detail: message,
    life: duration > 0 ? duration : undefined, // undefined makes it sticky
    closable: true
  });
};

/**
 * Show a global error message using PrimeVue Toast
 * @param {string} error - The error message to display
 * @param {number} duration - Auto-hide duration in milliseconds (0 = sticky)
 */
const showError = (error: string, duration: number = 0): void => {
  toast.add({
    severity: 'error',
    summary: t('Error'),
    detail: error,
    life: duration > 0 ? duration : undefined, // undefined makes it sticky
    closable: true
  });
};

/**
 * Clear global message (legacy method for compatibility)
 */
const clearGlobalMessage = (): void => {
  toast.removeAllGroups();
};

const clearGlobalError = (): void => {
  toast.removeAllGroups();
};

const clearAll = (): void => {
  toast.removeAllGroups();
};

// Store handler references for cleanup
let showHandler: (data?: string | MessagePayload) => void;
let errorHandler: (data?: string | ErrorPayload) => void;
let successHandler: (data?: string | MessagePayload) => void;
let warningHandler: (data?: string | MessagePayload) => void;
let clearHandler: () => void;
let clearMessageHandler: () => void;
let clearErrorHandler: () => void;

const runAction = (message: { event: string; payload?: unknown }): void => {
  toast.removeGroup('action');
  eventBus.emit(message.event, message.payload);
};

const showAction = (data: ActionPayload): void => {
  const type = data.type ?? 'warning';
  toast.add({
    severity: mapTypeToPrimevueSeverity(type),
    summary: getSummaryForType(type),
    detail: data.message,
    group: 'action',
    actionLabel: data.actionLabel,
    event: data.event,
    payload: data.payload,
  } as Parameters<typeof toast.add>[0]);
};

const setupEventListeners = (): void => {
  eventBus.on('globalMessage.action', showAction as (data: unknown) => void);
  showHandler = (data?: string | MessagePayload) => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.show event:', data);
    if (typeof data === 'string') {
      showMessage(data);
    } else if (typeof data === 'object' && data) {
      showMessage(data.message, data.type, data.duration);
    }
  };
  eventBus.on('globalMessage.show', showHandler);

  errorHandler = (data?: string | ErrorPayload) => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.error event:', data);
    if (typeof data === 'string') {
      showError(data);
    } else if (typeof data === 'object' && data) {
      showError(data.message, data.duration);
    }
  };
  eventBus.on('globalMessage.error', errorHandler);

  successHandler = (data?: string | MessagePayload) => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.success event:', data);
    if (typeof data === 'string') {
      showMessage(data, 'success');
    } else if (typeof data === 'object' && data) {
      showMessage(data.message, 'success', data.duration);
    }
  };
  eventBus.on('globalMessage.success', successHandler);

  warningHandler = (data?: string | MessagePayload) => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.warning event:', data);
    if (typeof data === 'string') {
      showMessage(data, 'warning');
    } else if (typeof data === 'object' && data) {
      showMessage(data.message, 'warning', data.duration);
    }
  };
  eventBus.on('globalMessage.warning', warningHandler);

  clearHandler = () => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.clear event');
    clearAll();
  };
  eventBus.on('globalMessage.clear', clearHandler);

  clearMessageHandler = () => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.clearMessage event');
    clearGlobalMessage();
  };
  eventBus.on('globalMessage.clearMessage', clearMessageHandler);

  clearErrorHandler = () => {
    log('GlobalMessages', 'setupEventListeners', 'Received globalMessage.clearError event');
    clearGlobalError();
  };
  eventBus.on('globalMessage.clearError', clearErrorHandler);

  log('GlobalMessages', 'setupEventListeners', 'Event listeners setup complete');
};

onMounted(() => {
  log('GlobalMessages', 'mounted', 'Component mounted, setting up event listeners');
  setupEventListeners();
});

onUnmounted(() => {
  log('GlobalMessages', 'beforeUnmount', 'Component unmounting, cleaning up event listeners');
  // Clean up event listeners
  eventBus.off('globalMessage.show', showHandler);
  eventBus.off('globalMessage.error', errorHandler);
  eventBus.off('globalMessage.success', successHandler);
  eventBus.off('globalMessage.warning', warningHandler);
  eventBus.off('globalMessage.action', showAction as (data: unknown) => void);
  eventBus.off('globalMessage.clear', clearHandler);
  eventBus.off('globalMessage.clearMessage', clearMessageHandler);
  eventBus.off('globalMessage.clearError', clearErrorHandler);
});
</script>

<style scoped>
.global-message-action {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}
/* 
 * GlobalMessages now uses PrimeVue Toast internally
 * All styling is handled by PrimeVue's theme system
 * Custom styles can be added here if needed for theme customization
 */
</style>